import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useRef, useState } from "react";
import {
  AppWindow,
  Box,
  BoxSelect,
  Check,
  ChevronDown,
  Circle,
  CircleDot,
  Combine,
  Cone,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileArchive,
  FilePlus2,
  FileUp,
  Grid3X3,
  Hammer,
  Home,
  Inspect,
  Info,
  LayoutGrid,
  Maximize2,
  Minus,
  Move3D,
  Orbit,
  PanelTop,
  Printer,
  Redo2,
  Rotate3D,
  Ruler,
  Save,
  Search,
  Sparkles,
  SquareDashed,
  Triangle,
  Undo2,
  X
} from "lucide-react";
import { createMotivePackage, motiveFilename, saveBlobWithPicker } from "../storage/motivePackage";
import { useModelStore } from "../state/store";
import { sendViewportCommand } from "../viewport/commands";
import { Viewport } from "../viewport/Viewport";
import { Brand } from "./ProjectHome";
import { NetworkMenus } from "./NetworkMenus";
import { ProfileMenu } from "./ProfileMenu";
import { FullscreenButton } from "./FullscreenButton";
import { AdRail } from "./AdRail";
import { CommandPalette } from "./CommandPalette";
import { DesignPanel } from "./DesignPanel";
import { ExportDialog } from "./ExportDialog";
import { MotiveMode } from "./MotiveMode";
import { NodeInspector } from "./NodeInspector";
import { PrintPanel } from "./PrintPanel";

export function Editor({ onHome, onOpenFile, onNew, onAbout }: { onHome: () => void; onOpenFile: () => void; onNew: () => void; onAbout: () => void }): React.JSX.Element {
  const [exportOpen, setExportOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [exportInitial, setExportInitial] = useState<"motive" | "step" | "3mf" | "stl" | "obj">("3mf");
  const mode = useModelStore((state) => state.mode);
  const document = useModelStore((state) => state.document);
  const renameDocument = useModelStore((state) => state.renameDocument);
  const openExport = (format: typeof exportInitial = "3mf"): void => { setExportInitial(format); setExportOpen(true); };

  useKeyboardShortcuts(() => openExport("3mf"), onOpenFile, onNew);

  if (mode === "motive") {
    return <><div className="motive-ad-layout"><MotiveMode onExport={() => openExport("3mf")} /><AdRail /></div><ExportDialog key={exportInitial} open={exportOpen} onOpenChange={setExportOpen} initialFormat={exportInitial} /></>;
  }

  return (
    <div className="editor-shell">
      <header className="editor-topbar">
        <button className="brand-button" onClick={onHome} title="Project home"><Brand /></button>
        <nav className="menu-bar application-menu" aria-label="Application menus">
          <FileMenu onHome={onHome} onOpenFile={onOpenFile} onNew={onNew} onExport={openExport} />
          <EditMenu />
          <CreateMenu />
          <ModifyMenu />
          <BooleanMenu />
          <ViewMenu />
          <button className="menu-trigger" onClick={() => setPrintOpen(true)}><Inspect size={14} /> Inspect</button>
          <button className="menu-trigger" onClick={onAbout}><Info size={14} /> About</button>
        </nav>
        <NetworkMenus />
      </header>

      <div className="tool-bar">
        <div className="document-title-wrap">
          <input className="document-title" value={document.name} aria-label="Model name" onChange={(event) => renameDocument(event.target.value)} />
          <span className="document-unit">mm</span>
        </div>
        <div className="tool-controls" role="group" aria-label="Modeling tools"><div className="tool-controls-inner">
        <TransformTools />
        <div className="toolbar-separator" />
        <PrimitiveButton type="box" icon={<Box size={18} />} label="Box" />
        <PrimitiveButton type="cylinder" icon={<CircleDot size={18} />} label="Cylinder" />
        <PrimitiveButton type="sphere" icon={<Circle size={18} />} label="Sphere" />
        <PrimitiveButton type="cone" icon={<Cone size={18} />} label="Cone" />
        <PrimitiveButton type="wedge" icon={<Triangle size={18} />} label="Wedge" />
        <div className="toolbar-separator" />
        <OperationButton type="hole" icon={<CircleDot size={18} />} label="Hole" />
        <OperationButton type="fillet" icon={<Sparkles size={18} />} label="Fillet" />
        <OperationButton type="chamfer" icon={<SquareDashed size={18} />} label="Chamfer" />
        <OperationButton type="mirror" icon={<Copy size={18} />} label="Mirror" />
        <OperationButton type="linearPattern" icon={<Grid3X3 size={18} />} label="Pattern" />
        </div></div>
        <div className="header-utilities" role="group" aria-label="Editor utilities">
          <TopbarIcon title="Command palette" onClick={() => useModelStore.getState().setCommandPaletteOpen(true)}><Search size={16} /></TopbarIcon>
          <FullscreenButton />
          <TopbarIcon title="Isometric view (0)" onClick={() => sendViewportCommand("iso")}><Orbit size={17} /></TopbarIcon>
          <TopbarIcon title="Print readiness" onClick={() => setPrintOpen(true)}><Printer size={17} /></TopbarIcon>
          <ProfileMenu onHome={onHome} onAbout={onAbout} />
        </div>
      </div>

      <div className="editor-main">
        <DesignPanel />
        <main className="viewport-column">
          <Viewport />
          <ViewNavigator />
        </main>
        <NodeInspector />
        <AdRail />
      </div>
      <HistoryStatusBar />

      <ExportDialog key={exportInitial} open={exportOpen} onOpenChange={setExportOpen} initialFormat={exportInitial} />
      <PrintPanel open={printOpen} onOpenChange={setPrintOpen} onExport={() => openExport("3mf")} />
      <CommandPalette />
    </div>
  );
}

function useKeyboardShortcuts(onExport: () => void, onOpen: () => void, onNew: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const editing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || target?.isContentEditable;
      // Do not interpret menu typeahead or dialog typing as modelling shortcuts.
      if (event.defaultPrevented || target?.closest('[role="menu"], [role="dialog"]')) return;
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === "k") { event.preventDefault(); useModelStore.getState().setCommandPaletteOpen(true); return; }
      if (command && event.key.toLowerCase() === "z") { event.preventDefault(); event.shiftKey ? useModelStore.getState().redo() : useModelStore.getState().undo(); return; }
      if (command && event.key.toLowerCase() === "y") { event.preventDefault(); useModelStore.getState().redo(); return; }
      if (command && event.key.toLowerCase() === "o") { event.preventDefault(); onOpen(); return; }
      if (command && event.key.toLowerCase() === "n") { event.preventDefault(); onNew(); return; }
      if (command && event.key.toLowerCase() === "e") { event.preventDefault(); onExport(); return; }
      if (editing) return;
      if (event.key === "Delete" || event.key === "Backspace") useModelStore.getState().deleteSelected();
      else if (event.key.toLowerCase() === "b") useModelStore.getState().addPrimitive("box");
      else if (event.key.toLowerCase() === "c") useModelStore.getState().addPrimitive("cylinder");
      else if (event.key.toLowerCase() === "h") useModelStore.getState().addOperation("hole");
      else if (event.key.toLowerCase() === "v") useModelStore.getState().setTransformMode("select");
      else if (event.key.toLowerCase() === "m") useModelStore.getState().setTransformMode("translate");
      else if (event.key.toLowerCase() === "r") useModelStore.getState().setTransformMode("rotate");
      else if (event.key.toLowerCase() === "s") useModelStore.getState().setTransformMode("scale");
      else if (event.key.toLowerCase() === "f") sendViewportCommand("fit");
      else if (event.key === "0") sendViewportCommand("iso");
      else if (event.key === "1") sendViewportCommand("front");
      else if (event.key === "2") sendViewportCommand("right");
      else if (event.key === "3") sendViewportCommand("top");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onExport, onNew, onOpen]);
}

function FileMenu({ onHome, onOpenFile, onNew, onExport }: { onHome: () => void; onOpenFile: () => void; onNew: () => void; onExport: (format: "motive" | "step" | "3mf" | "stl" | "obj") => void }): React.JSX.Element {
  const document = useModelStore((state) => state.document);
  const saveCopy = async (): Promise<void> => saveBlobWithPicker(createMotivePackage(document), motiveFilename(document), "ModelMotive project", ".motive");
  return <Menu label="File"><MenuItem icon={<Home size={15} />} label="Project home" onSelect={onHome} /><MenuItem icon={<FilePlus2 size={15} />} label="New model" shortcut="Ctrl N" onSelect={onNew} /><MenuItem icon={<FileUp size={15} />} label="Open .motive" shortcut="Ctrl O" onSelect={onOpenFile} /><MenuSeparator /><MenuItem icon={<Save size={15} />} label="Save a copy" onSelect={() => void saveCopy()} /><MenuItem icon={<FileArchive size={15} />} label="Export project" onSelect={() => onExport("motive")} /><MenuSeparator /><MenuItem icon={<Download size={15} />} label="Export model…" shortcut="Ctrl E" onSelect={() => onExport("3mf")} /></Menu>;
}

function EditMenu(): React.JSX.Element {
  const undo = useModelStore((state) => state.undo);
  const redo = useModelStore((state) => state.redo);
  const duplicate = useModelStore((state) => state.duplicateSelected);
  const deleteSelected = useModelStore((state) => state.deleteSelected);
  return <Menu label="Edit"><MenuItem icon={<Undo2 size={15} />} label="Undo" shortcut="Ctrl Z" onSelect={undo} /><MenuItem icon={<Redo2 size={15} />} label="Redo" shortcut="Ctrl Shift Z" onSelect={redo} /><MenuSeparator /><MenuItem icon={<Copy size={15} />} label="Duplicate" onSelect={duplicate} /><MenuItem icon={<X size={15} />} label="Delete" shortcut="Delete" onSelect={deleteSelected} /></Menu>;
}

function CreateMenu(): React.JSX.Element {
  const add = useModelStore((state) => state.addPrimitive);
  return <Menu label="Create"><MenuItem icon={<Box size={15} />} label="Box" shortcut="B" onSelect={() => add("box")} /><MenuItem icon={<CircleDot size={15} />} label="Cylinder" shortcut="C" onSelect={() => add("cylinder")} /><MenuItem icon={<Circle size={15} />} label="Sphere" onSelect={() => add("sphere")} /><MenuItem icon={<Cone size={15} />} label="Cone" onSelect={() => add("cone")} /><MenuItem icon={<Triangle size={15} />} label="Wedge" onSelect={() => add("wedge")} /></Menu>;
}

function ModifyMenu(): React.JSX.Element {
  const add = useModelStore((state) => state.addOperation);
  return <Menu label="Modify"><MenuItem icon={<Move3D size={15} />} label="Transform" onSelect={() => add("transform")} /><MenuItem icon={<CircleDot size={15} />} label="Hole" shortcut="H" onSelect={() => add("hole")} /><MenuItem icon={<Sparkles size={15} />} label="Fillet" onSelect={() => add("fillet")} /><MenuItem icon={<SquareDashed size={15} />} label="Chamfer" onSelect={() => add("chamfer")} /><MenuSeparator /><MenuItem icon={<Copy size={15} />} label="Mirror" onSelect={() => add("mirror")} /><MenuItem icon={<Grid3X3 size={15} />} label="Linear pattern" onSelect={() => add("linearPattern")} /><MenuItem icon={<LayoutGrid size={15} />} label="Circular pattern" onSelect={() => add("circularPattern")} /></Menu>;
}

function BooleanMenu(): React.JSX.Element {
  const add = useModelStore((state) => state.addBoolean);
  return <Menu label="Boolean"><MenuItem icon={<Combine size={15} />} label="Union selected" onSelect={() => add("union")} /><MenuItem icon={<Minus size={15} />} label="Subtract selected" onSelect={() => add("subtract")} /><MenuItem icon={<Grid3X3 size={15} />} label="Intersect selected" onSelect={() => add("intersect")} /></Menu>;
}

function ViewMenu(): React.JSX.Element {
  const viewport = useModelStore((state) => state.document.viewport);
  const updateViewport = useModelStore((state) => state.updateViewport);
  const hasMotiveControls = useModelStore((state) => state.document.exposedParameters.length > 0);
  return <Menu label="View"><MenuItem icon={<Orbit size={15} />} label="Isometric" shortcut="0" onSelect={() => sendViewportCommand("iso")} /><MenuItem icon={<PanelTop size={15} />} label="Top" shortcut="3" onSelect={() => sendViewportCommand("top")} /><MenuItem icon={<Maximize2 size={15} />} label="Fit all" shortcut="F" onSelect={() => sendViewportCommand("fit")} /><MenuSeparator /><MenuItem icon={viewport.projection === "orthographic" ? <Check size={15} /> : <BoxSelect size={15} />} label="Orthographic projection" onSelect={() => updateViewport({ projection: viewport.projection === "orthographic" ? "perspective" : "orthographic" })} /><MenuItem icon={viewport.showGrid ? <Eye size={15} /> : <EyeOff size={15} />} label="Show grid" onSelect={() => updateViewport({ showGrid: !viewport.showGrid })} /><MenuItem icon={viewport.showEdges ? <Eye size={15} /> : <EyeOff size={15} />} label="Show edges" onSelect={() => updateViewport({ showEdges: !viewport.showEdges })} /><MenuSeparator /><MenuItem icon={<Sparkles size={15} />} label="Motive mode" disabled={!hasMotiveControls} onSelect={() => useModelStore.getState().setMode("motive")} /></Menu>;
}

function Menu({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <DropdownMenu.Root><DropdownMenu.Trigger asChild><button className="menu-trigger">{label}<ChevronDown size={12} /></button></DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content className="dropdown-content" align="start" sideOffset={4}>{children}</DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root>;
}

function MenuItem({ icon, label, shortcut, onSelect, disabled }: { icon: React.JSX.Element; label: string; shortcut?: string; onSelect: () => void; disabled?: boolean }): React.JSX.Element {
  return <DropdownMenu.Item className="dropdown-item" onSelect={onSelect} disabled={disabled}><span>{icon}</span><strong>{label}</strong>{shortcut && <kbd>{shortcut}</kbd>}</DropdownMenu.Item>;
}

function MenuSeparator(): React.JSX.Element { return <DropdownMenu.Separator className="dropdown-separator" />; }

function TransformTools(): React.JSX.Element {
  const mode = useModelStore((state) => state.transformMode);
  const setMode = useModelStore((state) => state.setTransformMode);
  return <div className="transform-tools"><ToolButton active={mode === "select"} label="Select" shortcut="V" onClick={() => setMode("select")}><BoxSelect size={18} /></ToolButton><ToolButton active={mode === "translate"} label="Move" shortcut="M" onClick={() => setMode("translate")}><Move3D size={18} /></ToolButton><ToolButton active={mode === "rotate"} label="Rotate" shortcut="R" onClick={() => setMode("rotate")}><Rotate3D size={18} /></ToolButton><ToolButton active={mode === "scale"} label="Scale" shortcut="S" onClick={() => setMode("scale")}><Maximize2 size={18} /></ToolButton></div>;
}

function PrimitiveButton({ type, icon, label }: { type: "box" | "cylinder" | "sphere" | "cone" | "wedge"; icon: React.JSX.Element; label: string }): React.JSX.Element {
  const add = useModelStore((state) => state.addPrimitive);
  return <ToolButton label={label} onClick={() => add(type)}>{icon}</ToolButton>;
}

function OperationButton({ type, icon, label }: { type: "hole" | "fillet" | "chamfer" | "mirror" | "linearPattern"; icon: React.JSX.Element; label: string }): React.JSX.Element {
  const add = useModelStore((state) => state.addOperation);
  return <ToolButton label={label} onClick={() => add(type)}>{icon}</ToolButton>;
}

function ToolButton({ children, label, shortcut, active, onClick }: { children: React.ReactNode; label: string; shortcut?: string; active?: boolean; onClick: () => void }): React.JSX.Element {
  return <button className={`tool-button ${active ? "active" : ""}`} title={`${label}${shortcut ? ` (${shortcut})` : ""}`} onClick={onClick}>{children}<span>{label}</span></button>;
}

function TopbarIcon({ children, title, onClick }: { children: React.ReactNode; title: string; onClick: () => void }): React.JSX.Element {
  return <button className="icon-button topbar-icon" title={title} aria-label={title} onClick={onClick}>{children}</button>;
}

function ViewNavigator(): React.JSX.Element {
  return <div className="view-navigator"><button onClick={() => sendViewportCommand("top")}>TOP</button><div><button onClick={() => sendViewportCommand("left")}>L</button><button className="iso" onClick={() => sendViewportCommand("iso")}><Box size={17} /></button><button onClick={() => sendViewportCommand("right")}>R</button></div><button onClick={() => sendViewportCommand("front")}>FRONT</button></div>;
}

function HistoryStatusBar(): React.JSX.Element {
  const nodes = useModelStore((state) => state.document.nodes);
  const selected = useModelStore((state) => state.selectedNodeIds);
  const selectNode = useModelStore((state) => state.selectNode);
  const saveStatus = useModelStore((state) => state.saveStatus);
  const kernelStatus = useModelStore((state) => state.kernelStatus);
  const message = useModelStore((state) => state.statusMessage);
  const evaluation = useModelStore((state) => state.evaluation);
  return (
    <footer className="history-status-bar">
      <div className="timeline-scroll">
        {nodes.length === 0 ? <span className="timeline-empty">Feature history appears here</span> : nodes.map((node, index) => <button key={node.id} className={`timeline-node ${selected.includes(node.id) ? "selected" : ""} ${node.enabled ? "" : "suppressed"}`} onClick={(event) => selectNode(node.id, event.ctrlKey || event.metaKey)}><span>{index + 1}</span>{node.name}</button>)}
      </div>
      <div className="status-cluster">
        {evaluation && evaluation.bodies.length > 0 && <span className="model-stats"><Ruler size={13} /> {evaluation.bodies.length} bod{evaluation.bodies.length === 1 ? "y" : "ies"}</span>}
        <span className={`status-dot ${kernelStatus}`} />
        <span className="status-message">{message}</span>
        <span className={`save-state ${saveStatus}`}>{saveStatus}</span>
      </div>
    </footer>
  );
}
